import type { LocksResponse } from '@/lib/collections/locks';
import type { DonationsResponse } from '@/lib/collections/donations';
import { getDecimalsForMint } from '@/utils/tokenConfig';
import { humanAmountToUsdSync } from '@/utils/usdPricing';

// Leaderboard epoch: only locks created and donations made AFTER this Unix timestamp
// (seconds) are counted toward Selfless Points on the leaderboard.
// Set to 2026-04-19 00:00:00 UTC to reset the board to 0s.
export const LEADERBOARD_EPOCH = 1745020800;

// Points multiplier: points = usdValue * USD_POINTS_MULTIPLIER
// e.g. $1 USD worth of any token → 100 points (before duration/tier multipliers)
//
// Lock duration multiplier input:
// - `standard` locks: time ACTUALLY SERVED (now - ts, capped at the promised unlockAt - ts),
//   NOT the promised duration. This prevents a user from opening a lock nominally dated
//   years out and immediately claiming the high-tier multiplier (unlockAt is user-supplied).
// - `perma` locks: promised duration (unlockAt - ts), unchanged — they cannot be early-unlocked.
// See getLockDurationSecondsForScoring / calculateLockPoints below.
export const USD_POINTS_MULTIPLIER = 100;

export const LAMPORTS_PER_SOL = 1_000_000_000;

// Duration tiers in seconds → multiplier
export const DURATION_TIERS: Array<{ seconds: number; multiplier: number; label: string }> = [
  { seconds: 86400,        multiplier: 1,   label: '1 Day' },
  { seconds: 604800,       multiplier: 2,   label: '1 Week' },
  { seconds: 2592000,      multiplier: 5,   label: '1 Month' },
  { seconds: 7776000,      multiplier: 10,  label: '3 Months' },
  { seconds: 15552000,     multiplier: 20,  label: '6 Months' },
  { seconds: 31536000,     multiplier: 50,  label: '1 Year' },
  { seconds: 63072000,     multiplier: 100, label: '2 Years' },
  { seconds: 157680000,    multiplier: 250, label: '5 Years' },
];

/**
 * Linearly interpolates multiplier based on lock duration.
 * Below 1 day → 0 (no points). Above top tier → top multiplier.
 */
export function getDurationMultiplier(durationSeconds: number): number {
  if (durationSeconds <= 0) return 0;

  // Below minimum tier
  if (durationSeconds < DURATION_TIERS[0].seconds) {
    return DURATION_TIERS[0].multiplier * (durationSeconds / DURATION_TIERS[0].seconds);
  }

  // Above maximum tier
  if (durationSeconds >= DURATION_TIERS[DURATION_TIERS.length - 1].seconds) {
    return DURATION_TIERS[DURATION_TIERS.length - 1].multiplier;
  }

  // Find the two surrounding tiers and interpolate
  for (let i = 0; i < DURATION_TIERS.length - 1; i++) {
    const lower = DURATION_TIERS[i];
    const upper = DURATION_TIERS[i + 1];
    if (durationSeconds >= lower.seconds && durationSeconds < upper.seconds) {
      const t = (durationSeconds - lower.seconds) / (upper.seconds - lower.seconds);
      return lower.multiplier + t * (upper.multiplier - lower.multiplier);
    }
  }

  return DURATION_TIERS[DURATION_TIERS.length - 1].multiplier;
}

/**
 * Returns true if an early-unlocked lock has completed at least 90% of its timer.
 * Since we don't store an explicit "early unlocked at" timestamp, we use the current
 * time as a proxy. For a lock that was early-unlocked, (now - ts) represents at minimum
 * the elapsed time at the moment of unlock (since now >= unlock moment). If this ratio
 * is >= 0.9 relative to the total lock duration, the lock qualifies for selfless points.
 */
export function earlyUnlockedMeetsThreshold(lock: LocksResponse, nowSeconds?: number): boolean {
  const ts = lock.ts ?? 0;
  const unlockAt = lock.unlockAt ?? 0;
  const totalDuration = unlockAt - ts;
  if (totalDuration <= 0) return false;
  const now = nowSeconds ?? (Date.now() / 1000);
  const elapsed = now - ts;
  return elapsed / totalDuration >= 0.9;
}

/**
 * Returns the duration (in seconds) to use as input to the duration multiplier.
 *
 * SECURITY: `unlockAt` is fully user-supplied at lock-creation time, so scoring on the
 * *promised* duration (unlockAt - ts) lets a user open a lock nominally dated years out
 * and immediately claim the high-tier multiplier while it's still active. To close that,
 * standard locks are scored on time ACTUALLY SERVED so far: elapsed = now - ts, clamped
 * to [0, promisedDuration] so a matured (or early-unlocked, still-active-read) lock never
 * scores above its full promised term.
 *
 * `perma` locks keep the old behaviour (score on promised duration) — they cannot be
 * early-unlocked, so there is no way to bank a high multiplier without actually serving it.
 */
function getLockDurationSecondsForScoring(lock: LocksResponse, nowSeconds?: number): number {
  const ts = lock.ts ?? 0;
  const unlockAt = lock.unlockAt ?? 0;
  const promisedDuration = unlockAt - ts;

  if (lock.lockType === 'perma') {
    return promisedDuration;
  }

  const now = nowSeconds ?? (Date.now() / 1000);
  const elapsed = now - ts;
  // Clamp to [0, promisedDuration]: never negative, never above the promised term.
  return Math.max(0, Math.min(elapsed, promisedDuration));
}

/**
 * Calculate points for a single lock.
 * - Locks with status 'active' or 'unlocked' always earn points.
 * - Locks with status 'earlyUnlocked' only earn points if the timer was at least
 *   90% complete at the time of early unlock (approximated using current time).
 * USD-value-based: points are proportional to the USD value of locked tokens,
 * so $1 worth of any token (SOL, BONK, SKR) gives the same base points.
 * Uses the cached USD price (sync) — call usdPricing.getUsdPricePerToken to warm the cache first.
 *
 * Returns `null` (never 0) when the USD price for this lock's token is currently
 * unavailable — callers MUST treat null as "pending pricing", not as zero points.
 * The only legitimate 0 return is the early-unlock-below-threshold case above, which
 * stays a real 0 and is never confused with a pending/unknown price.
 *
 * Duration multiplier input:
 * - `standard` locks use time ACTUALLY SERVED (now - ts, capped at the promised duration),
 *   not the promised duration itself — see getLockDurationSecondsForScoring.
 * - `perma` locks use the promised duration (unlockAt - ts), unchanged from before.
 */
export function calculateLockPoints(lock: LocksResponse, nowSeconds?: number): number | null {
  // Early-unlocked locks that didn't reach 90% of their timer earn zero selfless points.
  // This is a genuine 0 and must stay distinguishable from "price unknown" below.
  if (lock.status === 'earlyUnlocked' && !earlyUnlockedMeetsThreshold(lock, nowSeconds)) {
    return 0;
  }
  const decimals = getDecimalsForMint(lock.tokenMint);
  const divisor = Math.pow(10, decimals);
  const tokenAmount = (lock.lkAmt ?? 0) / divisor;
  const usdValue = humanAmountToUsdSync(tokenAmount, lock.tokenMint ?? '');
  if (usdValue === null) return null;
  const durationSeconds = getLockDurationSecondsForScoring(lock, nowSeconds);
  const durationMultiplier = getDurationMultiplier(durationSeconds);
  const lockTypeMultiplier = lock.lockType === 'perma' ? 2 : 1;
  // points = usdValue * USD_POINTS_MULTIPLIER * durationMultiplier * lockTypeMultiplier
  return usdValue * USD_POINTS_MULTIPLIER * durationMultiplier * lockTypeMultiplier;
}

/**
 * Sums an array of possibly-null point values (null = USD price unavailable for that
 * item). Pending items are excluded from the sum entirely — never folded in as 0 — and
 * counted separately so callers can surface "N pending pricing" instead of silently
 * showing an understated total as if it were complete.
 */
export function sumPendingAwarePoints(values: Array<number | null>): { total: number; pendingCount: number } {
  let total = 0;
  let pendingCount = 0;
  for (const v of values) {
    if (v === null) pendingCount += 1;
    else total += v;
  }
  return { total, pendingCount };
}

export interface LeaderboardEntry {
  address: string;
  totalPoints: number;
  lockPoints: number;
  donationPoints: number;
  lockCount: number;
  permaCount: number;
  tokenMints: Set<string>; // which token mints this user has locked
  hasDonated: boolean;
  // Number of locks/donation batches excluded from the points above because their
  // USD price was unavailable at aggregation time. Never folded into totalPoints as 0.
  pendingCount: number;
}

// ─── Donation Points ─────────────────────────────────────────────────────────

/**
 * Calculate tier multiplier based on recipient count.
 * 10 users → 1.5x, 25 → 2.0x, 100 → 3.0x, 200+ → 5.0x (all users)
 */
export function getDonationTierMultiplier(recipientCount: number): number {
  if (recipientCount >= 200) return 5.0;
  if (recipientCount >= 100) return 3.0;
  if (recipientCount >= 25) return 2.0;
  return 1.5;
}

/**
 * Calculate points earned for a donation batch.
 * USD-value-based: points = usdValue * USD_POINTS_MULTIPLIER * tierMultiplier * selectedBonus.
 * This ensures $1 worth of SOL gives the same points as $1 worth of BONK or SKR.
 * Uses the cached USD price (sync) — call usdPricing.getUsdPricePerToken to warm the cache first.
 *
 * Returns `null` (never 0) when a tokenMint is supplied but its USD price is currently
 * unavailable — callers MUST treat null as "pending pricing", not as zero points.
 * The recipientCount<=0 / totalAmountHuman<=0 case is a genuine 0 and stays a real 0.
 *
 * @param totalAmountHuman - total donated in human units (e.g. 1.5 SOL not lamports)
 * @param recipientCount - number of unique recipients
 * @param selectedUserCount - how many were selected favorites
 * @param tokenMint - token mint used to look up USD price
 */
export function calculateDonationPoints(
  totalAmountHuman: number,
  recipientCount: number,
  selectedUserCount: number,
  tokenMint?: string,
): number | null {
  if (recipientCount <= 0 || totalAmountHuman <= 0) return 0;
  let usdValue: number;
  if (tokenMint) {
    const price = humanAmountToUsdSync(totalAmountHuman, tokenMint);
    if (price === null) return null;
    usdValue = price;
  } else {
    usdValue = totalAmountHuman; // fallback: treat as USD if no mint
  }
  const tierMultiplier = getDonationTierMultiplier(recipientCount);
  const selectedBonus = 1 + selectedUserCount * 0.3;
  return Math.round(usdValue * USD_POINTS_MULTIPLIER * tierMultiplier * selectedBonus);
}

function createLeaderboardEntry(address: string): LeaderboardEntry {
  return {
    address,
    totalPoints: 0,
    lockPoints: 0,
    donationPoints: 0,
    lockCount: 0,
    permaCount: 0,
    tokenMints: new Set<string>(),
    hasDonated: false,
    pendingCount: 0,
  };
}

/**
 * Aggregates all locks by owner and returns a sorted leaderboard array.
 * Only locks created at or after LEADERBOARD_EPOCH and donations at or after
 * LEADERBOARD_EPOCH are counted, so the board shows 0s for pre-epoch activity.
 *
 * Locks/donation batches whose USD price is unavailable (calculateLockPoints /
 * calculateDonationPoints returning null) are EXCLUDED from totalPoints, lockPoints,
 * and donationPoints — never folded in as 0 — and instead increment `pendingCount`
 * so the UI can show "N entries pending pricing" without silently understating a
 * user's real total.
 */
export function buildLeaderboard(locks: LocksResponse[], donations?: DonationsResponse[]): LeaderboardEntry[] {
  const map = new Map<string, LeaderboardEntry>();
  // Shared instant so every lock in this leaderboard build is scored against the same "now"
  // (time-served scoring for standard locks depends on now - ts).
  const nowSeconds = Date.now() / 1000;

  for (const lock of locks) {
    // Skip locks created before the epoch reset
    if ((lock.ts ?? 0) < LEADERBOARD_EPOCH) continue;

    const owner = lock.owner ?? '';
    if (!owner) continue;

    const entry = map.get(owner) ?? createLeaderboardEntry(owner);
    map.set(owner, entry);

    entry.lockCount += 1;
    if (lock.lockType === 'perma') entry.permaCount += 1;
    if (lock.tokenMint) entry.tokenMints.add(lock.tokenMint);

    const pts = calculateLockPoints(lock, nowSeconds);
    if (pts === null) {
      entry.pendingCount += 1;
    } else {
      entry.totalPoints += pts;
      entry.lockPoints += pts;
    }
  }

  // Add donation points if provided
  if (donations && donations.length > 0) {
    // Group by donor + batchId to calculate batch-level points
    const batchMap = new Map<string, { donor: string; recipients: DonationsResponse[]; tokenMint: string; totalAmount: number }>();
    for (const d of donations) {
      // Skip donations made before the epoch reset
      if ((d.ts ?? 0) < LEADERBOARD_EPOCH) continue;

      const key = `${d.donor}::${d.batchId}`;
      if (!batchMap.has(key)) {
        batchMap.set(key, { donor: d.donor, recipients: [], tokenMint: d.tokenMint, totalAmount: 0 });
      }
      const batch = batchMap.get(key)!;
      batch.recipients.push(d);
      batch.totalAmount += d.amount;
    }

    for (const batch of batchMap.values()) {
      const donor = batch.donor;
      if (!donor) continue;
      const decimals = getDecimalsForMint(batch.tokenMint);
      const divisor = Math.pow(10, decimals);
      const totalHuman = batch.totalAmount / divisor;
      // calculateDonationPoints now converts to USD internally using cached prices
      const pts = calculateDonationPoints(totalHuman, batch.recipients.length, 0, batch.tokenMint);

      const entry = map.get(donor) ?? createLeaderboardEntry(donor);
      map.set(donor, entry);

      entry.hasDonated = true;
      if (pts === null) {
        entry.pendingCount += 1;
      } else {
        entry.donationPoints += pts;
        entry.totalPoints += pts;
      }
    }
  }

  return Array.from(map.values()).sort((a, b) => b.totalPoints - a.totalPoints);
}

export function formatPoints(points: number | null): string {
  if (points === null) return '—';
  if (points < 1) return points.toFixed(2);
  return Math.round(points).toLocaleString('en-US');
}

export function truncateAddress(address: string): string {
  if (address.length <= 10) return address;
  return `${address.slice(0, 4)}...${address.slice(-4)}`;
}
